import { OreHeading, OreInput, OreStack } from '@edenredespana/oreneta'
import { useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { contractTranslation } from '@/Contract/ui/contractTranslation'
import { ContractForm } from '@/Contract/ui/hooks'
import { handleErrors } from '@/Shared/ui/form'
import { TransformObject, useGooglePlaces } from '@/Store/ui/hooks'

interface ContractBillingDataFormAddressProps {
  readOnly?: boolean
  show: boolean
}

export const ContractBillingDataFormAddress = ({
  readOnly = false,
  show
}: ContractBillingDataFormAddressProps): JSX.Element => {
  const { t } = useTranslation()

  const formContext = useFormContext<ContractForm>()
  const { register, formState } = formContext
  const { errors } = handleErrors(formState)
  const { ref, ...rest } = register('street')

  const transformObject: TransformObject<ContractForm> = {
    street: 'street',
    number: 'number',
    floorBlockStair: 'floorBlockStair',
    postalCode: 'postalCode',
    city: 'city',
    province: 'province',
  }

  const inputReference = useRef<HTMLInputElement | null>(null)

  const { initGooglePlaces } = useGooglePlaces({ formContext, transformObject })

  useEffect(() => {
    initGooglePlaces(inputReference)
  }, [])

  return (
    <OreStack placeContent="stretch" space="large">
      {!readOnly ? (
        <OreHeading as="h2" size="headline-md">
          {t(contractTranslation.billingData.form.address.title)}
        </OreHeading>
      ) : null}
      <OreStack direction="row" space="large" placeContent="stretch">
        <OreInput
          {...rest}
          {...errors('street')}
          id="street"
          label={t(contractTranslation.billingData.form.address.street.label)}
          placeholder={t(contractTranslation.billingData.form.address.street.placeholder)}
          required
          ref={(input: HTMLInputElement) => {
            inputReference.current = input
            ref(input)
          }}
          defaultValue={formContext.getValues('street') ?? ''}
          labelTooltip={
            !readOnly
              ? t(contractTranslation.billingData.form.address.street.tooltip)
              : undefined
          }
          maxLength={255}
          disabled={readOnly || !show}
        />
      </OreStack>
      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreInput
          {...register('number')}
          {...errors('number')}
          id="number"
          label={t(contractTranslation.billingData.form.address.number.label)}
          required
          disabled={readOnly || !show}
        />
        <OreInput
          {...register('floorBlockStair')}
          {...errors('floorBlockStair')}
          id="floorBlockStair"
          label={t(contractTranslation.billingData.form.address.floorBlockStair.label)}
          disabled={readOnly || !show}
        />
      </OreStack>
      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreInput
          {...register('postalCode')}
          {...errors('postalCode')}
          id="postalCode"
          label={t(contractTranslation.billingData.form.address.postalCode.label)}
          required
          disabled={readOnly || !show}
        />
        <OreInput
          {...register('city')}
          {...errors('city')}
          id="city"
          label={t(contractTranslation.billingData.form.address.city.label)}
          required
          disabled={readOnly || !show}
        />
        <OreInput
          {...register('province')}
          {...errors('province')}
          id="province"
          label={t(contractTranslation.billingData.form.address.province.label)}
          required
          disabled={readOnly || !show}
        />
      </OreStack>
      <OreStack direction="row" space="large" placeContent="stretch">
        <OreInput
          {...register('additionalInformation')}
          {...errors('additionalInformation')}
          id="additionalInformation"
          label={t(contractTranslation.billingData.form.address.additionalInfo.label)}
          placeholder={t(
            contractTranslation.billingData.form.address.additionalInfo.placeholder,
          )}
          maxLength={255}
          disabled={readOnly || !show}
        />
      </OreStack>
    </OreStack>
  )
}
